<script setup lang="ts">
import { useField } from 'vee-validate';
import TextField from './TextField.vue';
import type { VeeTextFieldProps } from '../types';
import { toRef, computed } from 'vue';
import { useVeeFormProps } from '../utils';

const props = defineProps<VeeTextFieldProps>();

const veeProps = useVeeFormProps(props);

const { errorMessage, value, handleChange, handleBlur, meta } = useField<string | undefined>(
  toRef(() => props.name),
  undefined,
  {
    validateOnValueUpdate: false,
  },
);

function onUpdate(input: string | undefined) {
  // An empty API value is null. The text field reports that as "", which normalizes to undefined.
  if (input === value.value || (input == null && value.value == null)) return;
  handleChange(input, meta.touched);
}

function onBlur(event: FocusEvent) {
  handleBlur(event, true);
}

const modelValue = computed(() =>
  value.value &&
  props.outputConverter &&
  (veeProps.value.mode === 'details' || veeProps.value.disabled === true)
    ? props.outputConverter(value.value)
    : value.value,
);

const slots = defineSlots<{ [key: string]: unknown }>();
</script>

<template>
  <TextField
    v-bind="veeProps"
    :modelValue="modelValue"
    :errorMessage="errorMessage"
    @update:modelValue="onUpdate"
    @blur="onBlur"
  >
    <template v-for="(_, name) in slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
  </TextField>
</template>
